import React, { useEffect, useMemo } from 'react';
import { AttributeObjectListType, AttributeSelectedType, CompositeLensProduct, CompositeLensVariationType, PatternReplacement } from '../../constants/dataTypes';
import { convertFormatWithRegex } from 'functions/convertFormatWithRegex';
import { titleCase } from 'functions/capitalize';
import { useGlassesStore } from 'zus/glassesStore';
import { parseAsString } from 'nuqs';
import { useQueryState } from 'nuqs';

interface VariationSelectorProps {
	attributesToShow: string[];
	lensProducts: CompositeLensVariationType[];
	selectedVariations: AttributeSelectedType;
	// eslint-disable-next-line no-unused-vars
	setSelectedVariations: (value: AttributeSelectedType) => void; // Updated this line
}

export default function VariationSelector({
	lensProducts,
	attributesToShow,
	selectedVariations,
	setSelectedVariations,
}: VariationSelectorProps) {
	const [valueSelected, setValueSelected] = React.useState(false);


	const [frameShape] = useQueryState('shape', parseAsString.withDefault('classic'));
	const patternReplacements: PatternReplacement[] = [
		{ pattern: /^(\d+)-(\d{2})$/, replacement: "$1.$2", addPositiveSign: true },
		// Add more pattern-replacement pairs here if needed
	];

	const updateData = useGlassesStore((state) => state.updateData);

	const convertPower = (power: string) => {
		return '+' + power.replace('-', '.');
	}

	const getDresdenGoPower = () => {
		if (updateData && updateData.type === 'dresdengo' && updateData.glassesData && updateData.glassesData.lensData && updateData.glassesData.lensData.attributes) {
			const lensDataAttr = updateData.glassesData.lensData.attributes;
			if (lensDataAttr.attribute_pa_power) {
				return convertPower(lensDataAttr.attribute_pa_power);
			}
		}

		return false;
	}

	function checkVariationValueSelected(attributeKey: string, value: string) {
		if (getDresdenGoPower() === value && !valueSelected) return true;
		if (!selectedVariations || !selectedVariations[attributeKey]) return false;

		return selectedVariations[attributeKey].value === value;
	}

	//lensProducts is an array of CompositeLensProduct and each product has variations. Each variation has meta object which contains all the attributes and their values.
	//Example for variation: 0:meta
	//	attribute_pa_power:"1-00"
	//	attribute_pa_treatment:"reading"
	//we want to create a function to return a list of all attributes and their values for each product
	//Example:
	//	attribute_pa_power:["1-00"]

	// Function to convert a single string
	function convertAttribute(s: string): string {
		if (s.startsWith("attribute_pa_")) {
			return titleCase(s.replace("attribute_pa_", ""));
		}
		return s; // return the original string if it doesn't match the prefix
	}

	function getVariationAttributes(variations: CompositeLensVariationType[]) {

		const attributes: AttributeObjectListType = variations.reduce((acc, variation) => {
			const keys = Object.keys(variation.meta).filter((key) => key.startsWith('attribute_pa_') && key !== 'attribute_pa_treatment' && key !== 'attribute_pa_frame_shape');
			keys.forEach((key) => {
				if (attributesToShow.includes(key)) {
					if(variation.meta['attribute_pa_frame_shape' as keyof typeof variation.meta] == frameShape) {
						const value = variation.meta[key as keyof typeof variation.meta] as string;
						if (value == 'no-power') return;
						if (acc[key]) {
							acc[key].push({ variation, value });
						} else {
							acc[key] = [{ variation, value }];
						}
					}
				}
			});
			return acc;
		}, {} as AttributeObjectListType);

		//convertFormatWithRegex(  for each key in attributes, convert the format of the values
		for (const key in attributes) {
			attributes[key] = convertFormatWithRegex(attributes[key], patternReplacements);
		}
		return attributes;
	}

	const attributeList = useMemo(() => {
		if (!lensProducts) return {};
		return getVariationAttributes(lensProducts);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lensProducts, frameShape]);

	//when frameShape is changed, we want to call setSelectedVariations with the attributeItem that has the same 'value' for the previous frameShape
	useEffect(() => {
		const keys = Object.keys(selectedVariations);
		if(keys.length > 0) {
			keys.forEach((key) => {
				if (attributeList[key]) {
				const attributeItem = attributeList[key].find((item) => item.value === selectedVariations[key].value);
				if (attributeItem) {
					setSelectedVariations({
						...selectedVariations,
						[key]: attributeItem
					});
					}
				}
			});
		}
	}, [frameShape, attributeList]);

	//attributeList is an object that contains keys and its values.
	//we want to create a list of all possible values. use key for title and values for list
	return (
		<div className="w-full select-none">
			<div className="overflow-hidden bg-body">
				<div className="flex flex-col gap-6">
					{Object.keys(attributeList).map((title) => (
						<div key={title} className="flex flex-col gap-4">
							<div className="font-medium tracking-wider text-[#616161] text-xs font-sans">
								{convertAttribute(title)}
							</div>
							<div className="flex gap-4">
								<div className="hide-scroll flex overflow-auto w-full gap-x-1.5 gap-y-2">
									{attributeList[title].map((attributeItem: any, index: number) => {
										var check = true;
										return (
											<div
												key={index}
												onClick={() => {
													if (check) {
														console.log('attributeItem', attributeItem);
														setValueSelected(true);
														setSelectedVariations({
															...selectedVariations,
															[title]: attributeItem
														});
													}

												}}
												className={`${check ? '' : 'disable-size text-[#DEDEDE]'} cursor-pointer flex items-center justify-center bg-white border rounded-md w-[54px] h-[46px] p-1 xsmx:h-[53px] 
											${checkVariationValueSelected(title, attributeItem.value)
														? 'border-black'
														: 'border-[#CACACA] hover:border-[#AEADAD]'
													} duration-300 transition-colors`}>
												<span className="font-sans font-medium text-sm text-center xsmx:text-xs">
													{attributeItem.value}
												</span>
											</div>
										)
									})}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}